/**
 * "Over time": one daily metric (plays, watch time or viewers, chosen above the chart) across the
 * range, with the previous period as the faint line behind it, day 1 against day 1. Its table and
 * CSV hold both periods, day by day.
 */

import { useState } from "react";
import Segmented from "@web-ui/components/Segmented";
import type { DayPoint } from "../api";
import { LineChart } from "../charts/line-chart";
import type { Table } from "../csv";
import { definition, type MetricId } from "../definitions";
import { formatFigure } from "../figures";
import { dayLabel, duration } from "../format";
import { alignToDays, eachDay, endsToday } from "../range";
import type { ReportScope } from "../scope";
import { NoData } from "./no-data";
import { ReportCard } from "./report-card";

type Daily = "plays" | "watchSeconds" | "viewers";
const DAILY: Daily[] = ["plays", "watchSeconds", "viewers"];

interface Row { day: string; value: number; previousDay: string; previousValue: number | null }

export function TrendCard({ scope, current, before, what, empty, emptyAction, height }: {
  scope: Pick<ReportScope, "brand" | "pack" | "range" | "previous">;
  current: readonly DayPoint[];
  /** The previous period's days, or null when not (yet) known. */
  before: readonly DayPoint[] | null;
  /** Names the file ("overview", an item's name). */
  what: string;
  /** Nothing watched in the range: say why instead of a flat line. */
  empty?: boolean;
  /** The one thing to do when it is empty (publish first, when nothing is live). */
  emptyAction?: { label: string; href: string };
  /** The chart's height in pixels (the line chart's own default when absent). */
  height?: number;
}) {
  const [metric, setMetric] = useState<Daily>("plays");
  const def = definition(metric as MetricId, scope.pack);
  const days = eachDay(scope.range);
  const previousDays = eachDay(scope.previous);
  const values = alignToDays(current, scope.range, (p) => p[metric]);
  // A previous period with nothing in it is no comparison: no faint line along zero.
  const previousAligned = before ? alignToDays(before, scope.previous, (p) => p[metric]) : null;
  const previousValues = previousAligned?.some((v) => v > 0) ? previousAligned : null;
  const format = (v: number) => formatFigure(metric as MetricId, v);
  const tick = metric === "watchSeconds" ? (v: number) => (v >= 3600 ? `${Math.round(v / 3600)} h` : duration(v)) : format;

  const rows: Row[] = days.map((day, i) => ({ day, value: values[i]!, previousDay: previousDays[i]!, previousValue: previousValues?.[i] ?? null }));
  const unit = metric === "watchSeconds" ? " (seconds)" : "";
  const table: Table<Row> = {
    columns: [
      { label: "Day", value: (r) => r.day, format: (_, r) => dayLabel(r.day) },
      { label: def.label, csvLabel: `${def.label}${unit}`, value: (r) => r.value, format: (v) => format(Number(v)), numeric: true },
      { label: "Same day, previous period", csvLabel: "Previous period day", value: (r) => r.previousDay, format: (_, r) => dayLabel(r.previousDay) },
      { label: `${def.label}, previous period`, csvLabel: `${def.label}, previous period${unit}`, value: (r) => r.previousValue, format: (v) => (v === null ? "—" : format(Number(v))), numeric: true },
    ],
    rows,
  };
  const total = values.reduce((a, b) => a + b, 0);
  const peak = values.indexOf(Math.max(...values));
  const description = `${def.label} per day from ${dayLabel(scope.range.from)} to ${dayLabel(scope.range.to)}${metric !== "viewers" ? `, ${format(total)} in all` : ""}; the highest was ${format(values[peak] ?? 0)} on ${dayLabel(days[peak] ?? scope.range.to)}.${previousValues ? ` The grey line is the ${days.length} days before.` : ""}`;

  // Today is still going: its point says so, and the line's last step is dashed.
  const partial = endsToday(scope.range);
  const pointLabels = days.map((d, i) => (partial && i === days.length - 1 ? `${dayLabel(d)} · so far` : dayLabel(d)));

  return (
    <ReportCard title="Over time" description="Each day of the range, with the same days of the previous period behind it." table={table}
      file={{ brand: scope.brand, what: `${what} ${metric}`, range: scope.range }}
      empty={empty ? <NoData action={emptyAction} /> : undefined}
      actions={<Segmented className="w-auto" label="Show" size="sm" options={DAILY.map((id) => ({ id, label: definition(id, scope.pack).label }))} value={metric} onChange={setMetric} />}>
      <LineChart height={height} partialLast={partial} title={`${def.label} per day`} description={description} labels={days.map(dayLabel)} format={format} tickFormat={tick}
        series={[
          ...(previousValues ? [{ id: "previous", label: "Previous period", values: previousValues, kind: "previous" as const, pointLabels: previousDays.map(dayLabel) }] : []),
          { id: "current", label: "This period", values, kind: "current" as const, pointLabels },
        ]} />
    </ReportCard>
  );
}
